/**
 * 项目相关 API
 */
import apiClient from './config';
import {
  Project,
  ProjectType,
  ProjectStatus,
  ProjectBudgetLabor,
  ProjectBudgetTravel,
  ProjectBudgetOutsource,
  ProjectExpenseLabor,
  ProjectExpenseTravel,
  ProjectExpenseOutsource,
  ProjectChange,
  ProjectStage,
  ProjectMilestone,
} from '@/types';
import { createRequestDeduper, type RequestDedupOptions } from './request-dedupe';
import { createLogger } from '@/utils/logger';

const logger = createLogger('ProjectsApi');

type GenericRecord = Record<string, unknown>;

interface ApiErrorLike {
  response?: {
    status?: number;
    data?: {
      error?: string;
    };
  };
  data?: {
    error?: string;
  };
  message?: string;
}

interface ProjectBudgetData {
  labor: ProjectBudgetLabor[];
  travel: ProjectBudgetTravel[];
  outsource: ProjectBudgetOutsource[];
}

interface ProjectExpenseData {
  labor: ProjectExpenseLabor[];
  travel: ProjectExpenseTravel[];
  outsource: ProjectExpenseOutsource[];
}

interface ProjectMemberData {
  id: string;
  name: string;
  department?: string | null;
  employeeLevel?: string | null;
  avatar?: string | null;
  avatarUrl?: string | null;
  [key: string]: unknown;
}

interface ProjectMembersResult {
  success: boolean;
  data: ProjectMemberData[];
  error?: string;
}

interface ProjectProgressData {
  stages: ProjectStage[];
  milestones: ProjectMilestone[];
}

function getApiErrorLike(error: unknown): ApiErrorLike {
  if (typeof error === 'object' && error !== null) {
    return error as ApiErrorLike;
  }
  return {};
}

function getErrorMessage(error: unknown, fallback: string): string {
  const err = getApiErrorLike(error);
  return err.response?.data?.error || err.data?.error || err.message || fallback;
}

// 项目查询参数
export interface GetProjectsParams {
  name?: string;
  type?: ProjectType[];
  status?: ProjectStatus[];
  group?: string[];
  clientDept?: string[]; // 客户部筛选
  manager?: string[]; // 项目经理筛选
  frameworkId?: string; // 框架协议ID（计件制项目筛选）
  ids?: string[]; // 项目ID列表筛选（用于批量获取）
  showArchived?: boolean;
  scope?: 'system_permission' | 'all'; // 权限过滤范围：system_permission=仅系统权限，all=系统权限+项目成员权限
  skipCount?: boolean; // 是否跳过 total 统计（用于性能优化场景）
  includeActualEndDate?: boolean; // 是否返回实际结束日期（默认 true）
  includeHoursSummary?: boolean; // 是否返回工时汇总（默认 true）
  current?: number;
  pageSize?: number;
}

// 项目查询结果
export interface GetProjectsResult {
  data: Project[];
  total: number;
}

interface GetProjectByIdParams {
  includeHoursSummary?: boolean;
}

export type ProjectCostSection = 'labor' | 'travel' | 'outsource';

interface GetProjectCostParams {
  section?: ProjectCostSection;
}

// 项目创建/更新数据
export interface ProjectCreateInput {
  name: string;
  type: ProjectType;
  // P3-1: 驻场制项目不需要项目经理，设为可选
  managerId?: string;
  managerName?: string;
  group: string;
  bizManager?: string;
  clientDept?: string;
  planStartDate?: string;
  planEndDate?: string;
  contractAmount?: number;
  // 计件制特殊字段
  demandCode?: string;
  demandName?: string;
  frameworkId?: string;
  isPendingEntry?: boolean; // P0-2: 计件制项目待补录标记
  // 预算
  laborBudget?: GenericRecord[];
  travelBudget?: GenericRecord[];
  outsourceBudget?: GenericRecord[];
  // 阶段
  stages?: GenericRecord[];
}

/**
 * 获取项目列表
 */
async function fetchProjects(
  params: GetProjectsParams = {}
): Promise<GetProjectsResult> {
  const response = (await apiClient.get('/projects', {
    params,
  })) as unknown as {
    success: boolean;
    data: Project[];
    total: number;
  };
  return {
    data: response.data || [],
    total: response.total || 0,
  };
}

const dedupedGetProjects = createRequestDeduper<GetProjectsParams, GetProjectsResult>(
  fetchProjects,
  { defaultTtlMs: 3000 }
);

export async function getProjects(
  params: GetProjectsParams = {},
  options: RequestDedupOptions = {}
): Promise<GetProjectsResult> {
  return dedupedGetProjects(params, options);
}

/**
 * 获取可用于成本录入的项目列表
 * 使用 admin client 绕过 RLS，仅检查成本录入权限
 */
export async function getProjectsForCostEntry(): Promise<GetProjectsResult> {
  const response = (await apiClient.get('/costs/projects-for-entry')) as unknown as {
    success: boolean;
    data: Project[];
    total: number;
  };
  return {
    data: response.data || [],
    total: response.total || 0,
  };
}

/**
 * 获取项目详情
 */
export async function getProjectById(
  id: string,
  params: GetProjectByIdParams = {}
): Promise<Project | null> {
  try {
    const response = (await apiClient.get(`/projects/${id}`, {
      params,
    })) as unknown as {
      success: boolean;
      data: Project;
      error?: string;
    };
    if (response.success && response.data) {
      return response.data;
    }
    return null;
  } catch (error: unknown) {
    const err = getApiErrorLike(error);
    // 如果是 404 错误（项目不存在），返回 null
    if (
      err.response?.status === 404 ||
      err.message?.includes('项目不存在') ||
      err.message?.includes('404')
    ) {
      return null;
    }
    // 其他错误，重新抛出
    throw error;
  }
}

/**
 * 创建项目
 */
export async function createProject(
  data: ProjectCreateInput
): Promise<Project> {
  const response = (await apiClient.post('/projects', data)) as unknown as {
    success: boolean;
    data: Project;
  };
  return response.data;
}

/**
 * 更新项目（通过变更流程）
 */
export async function updateProject(
  id: string,
  data: ProjectCreateInput
): Promise<Project> {
  const response = (await apiClient.put(
    `/projects/${id}`,
    data
  )) as unknown as {
    success: boolean;
    data: Project;
  };
  return response.data;
}

/**
 * 获取项目预算
 */
export async function getProjectBudgets(
  id: string,
  params: GetProjectCostParams = {}
): Promise<ProjectBudgetData> {
  try {
    const response = (await apiClient.get(
      `/projects/${id}/budgets`,
      { params }
    )) as unknown as {
      success: boolean;
      data: ProjectBudgetData;
      error?: string;
    };
    if (response.success && response.data) {
      return response.data;
    }
    logger.warn('获取项目预算失败', response);
    return { labor: [], travel: [], outsource: [] };
  } catch (error: unknown) {
    logger.error('获取项目预算失败', error);
    return { labor: [], travel: [], outsource: [] };
  }
}

/**
 * 获取项目支出
 */
export async function getProjectExpenses(
  id: string,
  params: GetProjectCostParams = {}
): Promise<ProjectExpenseData> {
  try {
    const response = (await apiClient.get(
      `/projects/${id}/expenses`,
      { params }
    )) as unknown as {
      success: boolean;
      data: ProjectExpenseData;
      error?: string;
    };
    if (response.success && response.data) {
      return response.data;
    }
    logger.warn('获取项目支出失败', response);
    return { labor: [], travel: [], outsource: [] };
  } catch (error: unknown) {
    logger.error('获取项目支出失败', error);
    return { labor: [], travel: [], outsource: [] };
  }
}

/**
 * 获取项目变更记录
 */
export async function getProjectChanges(id: string): Promise<ProjectChange[]> {
  try {
    const response = (await apiClient.get(
      `/projects/${id}/changes`
    )) as unknown as {
      success: boolean;
      data: ProjectChange[];
      error?: string;
    };
    if (response.success && response.data) {
      return response.data;
    }
    logger.warn('获取项目变更记录失败', response);
    return [];
  } catch (error: unknown) {
    logger.error('获取项目变更记录失败', error);
    return [];
  }
}

/**
 * 提交项目变更申请
 */
export async function submitProjectChange(
  id: string,
  data: {
    changeType?: string;
    description: string;
    attachmentUrl: string;
    [key: string]: unknown;
  }
): Promise<GenericRecord> {
  const response = (await apiClient.post(
    `/projects/${id}/changes`,
    data
  )) as unknown as {
    success: boolean;
    data: GenericRecord;
  };
  return response.data;
}

/**
 * 提交独立计划变更申请（change_scope=plan_only）
 */
export async function submitProjectPlanChange(
  id: string,
  data: {
    description: string;
    attachmentUrl?: string;
    changeType?: string;
    changeDate?: string;
    stages?: GenericRecord[];
    milestones?: GenericRecord[];
    [key: string]: unknown;
  }
): Promise<GenericRecord> {
  const response = (await apiClient.post(
    `/projects/${id}/plan-changes`,
    data
  )) as unknown as {
    success: boolean;
    data: GenericRecord;
  };
  return response.data;
}

/**
 * 提交项目补录申请
 */
export async function submitPendingEntry(
  id: string,
  data: {
    contractAmount: number;
    laborBudget?: GenericRecord[];
    travelBudget?: GenericRecord[];
    outsourceBudget?: GenericRecord[];
    stages?: GenericRecord[];
  }
): Promise<GenericRecord> {
  const response = (await apiClient.post(
    `/projects/${id}/pending-entry`,
    data
  )) as unknown as {
    success: boolean;
    data: GenericRecord;
  };
  return response.data;
}

/**
 * 提交设计确认申请
 */
export async function submitDesignConfirm(
  id: string,
  data: {
    description?: string;
    attachmentUrl: string;
  }
): Promise<GenericRecord> {
  const response = (await apiClient.post(
    `/projects/${id}/design-confirm`,
    data
  )) as unknown as {
    success: boolean;
    data: GenericRecord;
  };
  return response.data;
}

/**
 * 提交项目归档申请
 */
export async function submitArchive(
  id: string,
  data: {
    description?: string;
    costsConfirmed?: boolean;
  }
): Promise<GenericRecord> {
  const response = (await apiClient.post(
    `/projects/${id}/archive`,
    data
  )) as unknown as {
    success: boolean;
    data: GenericRecord;
  };
  return response.data;
}

/**
 * 获取归档前成本确认信息
 */
export async function getArchiveCostSummary(id: string): Promise<{
  laborExpenseTotal: number;
  travelExpenseTotal: number;
  outsourceExpenseTotal: number;
  laborExpenseLastUpdatedAt: string | null;
  travelExpenseLastUpdatedAt: string | null;
  outsourceExpenseLastUpdatedAt: string | null;
}> {
  const response = (await apiClient.get(
    `/projects/${id}/archive`
  )) as unknown as {
    success: boolean;
    data: {
      laborExpenseTotal: number;
      travelExpenseTotal: number;
      outsourceExpenseTotal: number;
      laborExpenseLastUpdatedAt: string | null;
      travelExpenseLastUpdatedAt: string | null;
      outsourceExpenseLastUpdatedAt: string | null;
    };
  };
  return response.data;
}

/**
 * 取消项目归档
 */
export async function cancelArchive(id: string): Promise<GenericRecord> {
  const response = (await apiClient.post(
    `/projects/${id}/cancel-archive`
  )) as unknown as {
    success: boolean;
    data: GenericRecord;
  };
  return response.data;
}

/**
 * 更新阶段进度
 */
export async function updateStageProgress(
  id: string,
  data: {
    stages: GenericRecord[];
  }
): Promise<GenericRecord> {
  const response = (await apiClient.put(
    `/projects/${id}/stages`,
    data
  )) as unknown as {
    success: boolean;
    data: GenericRecord;
  };
  return response.data;
}

/**
 * 获取项目阶段列表
 */
export async function getProjectStages(projectId: string): Promise<ProjectStage[]> {
  try {
    const response = (await apiClient.get(
      `/projects/${projectId}/stages`
    )) as unknown as {
      success: boolean;
      data: ProjectStage[];
      error?: string;
    };
    if (response.success && response.data) {
      return response.data;
    }
    logger.warn('获取项目阶段失败', response);
    return [];
  } catch (error: unknown) {
    logger.error('获取项目阶段失败', error);
    return [];
  }
}

/**
 * 获取项目里程碑列表
 */
export async function getProjectMilestones(projectId: string): Promise<ProjectMilestone[]> {
  try {
    const response = (await apiClient.get(
      `/projects/${projectId}/milestones`
    )) as unknown as {
      success: boolean;
      data: ProjectMilestone[];
      error?: string;
    };
    if (response.success && response.data) {
      return response.data;
    }
    logger.warn('获取项目里程碑失败', response);
    return [];
  } catch (error: unknown) {
    logger.error('获取项目里程碑失败', error);
    return [];
  }
}

interface GetProjectProgressParams {
  projectId: string;
  includeTaskStats?: boolean;
  bypassCache?: boolean;
}

async function fetchProjectProgress(
  params: GetProjectProgressParams
): Promise<ProjectProgressData> {
  const response = (await apiClient.get(`/projects/${params.projectId}/progress`, {
    params: {
      include_task_stats: params.includeTaskStats === true,
      bypass_cache: params.bypassCache === true,
    },
  })) as unknown as {
    success: boolean;
    data?: ProjectProgressData;
    error?: string;
  };

  if (response.success && response.data) {
    return response.data;
  }

  logger.warn('获取项目进度聚合数据失败', response);
  return { stages: [], milestones: [] };
}

const dedupedGetProjectProgress = createRequestDeduper<
  GetProjectProgressParams,
  ProjectProgressData
>(fetchProjectProgress, { defaultTtlMs: 3000 });

export async function getProjectProgress(
  projectId: string,
  options: {
    includeTaskStats?: boolean;
    bypassCache?: boolean;
  } = {},
  requestOptions: RequestDedupOptions = {}
): Promise<ProjectProgressData> {
  return dedupedGetProjectProgress(
    {
      projectId,
      includeTaskStats: options.includeTaskStats === true,
      bypassCache: options.bypassCache === true,
    },
    requestOptions
  );
}

/**
 * 保存项目里程碑（差量保存）
 */
export async function saveProjectMilestones(
  projectId: string,
  milestones: Array<{
    id?: string;
    stageId?: string | null;
    name: string;
    weight: number;
    plannedCompletedDate?: string | null;
    sortOrder?: number;
  }>
): Promise<ProjectMilestone[]> {
  const response = (await apiClient.put(
    `/projects/${projectId}/milestones`,
    { milestones }
  )) as unknown as {
    success: boolean;
    data: ProjectMilestone[];
  };
  return response.data || [];
}

/**
 * 保存项目里程碑排序（拖拽落库）
 */
export async function saveProjectMilestoneSortOrders(
  projectId: string,
  orders: Array<{ id: string; sortOrder: number }>
): Promise<ProjectMilestone[]> {
  const response = (await apiClient.put(
    `/projects/${projectId}/milestones/sort`,
    { orders }
  )) as unknown as {
    success: boolean;
    data: ProjectMilestone[];
  };
  return response.data || [];
}

function buildMilestoneCompletionIdempotencyKey(
  projectId: string,
  milestoneId: string,
  data: {
    actualCompletedDate: string;
    attachmentUrl: string;
  }
): string {
  const raw = [
    projectId,
    milestoneId,
    (data.actualCompletedDate || '').trim(),
    (data.attachmentUrl || '').trim(),
  ].join('|');

  let hash = 2166136261;
  for (let i = 0; i < raw.length; i += 1) {
    hash ^= raw.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `mc_${(hash >>> 0).toString(16)}`;
}

/**
 * 提交里程碑完成审批
 */
export async function submitProjectMilestoneCompletion(
  projectId: string,
  milestoneId: string,
  data: {
    actualCompletedDate: string;
    attachmentUrl: string;
    description?: string;
  }
): Promise<{ approvalId: string; projectChangeId: string; milestoneId: string }> {
  const idempotencyKey = buildMilestoneCompletionIdempotencyKey(projectId, milestoneId, data);
  const response = (await apiClient.post(
    `/projects/${projectId}/milestones/${milestoneId}/submit-completion`,
    {
      ...data,
      idempotencyKey,
    }
  )) as unknown as {
    success: boolean;
    data: { approvalId: string; projectChangeId: string; milestoneId: string };
  };
  return response.data;
}

/**
 * 获取项目成员列表
 */
export async function getProjectMembers(projectId: string): Promise<ProjectMembersResult> {
  try {
    const response = (await apiClient.get(
      `/projects/${projectId}/members`
    )) as unknown as {
      success: boolean;
      data: ProjectMemberData[];
      error?: string;
    };
    if (response.success) {
      return { success: true, data: response.data || [] };
    }
    logger.warn('获取项目成员失败', response);
    return { success: false, data: [], error: response.error };
  } catch (error: unknown) {
    logger.error('获取项目成员失败', error);
    return { success: false, data: [], error: getErrorMessage(error, '获取项目成员失败') };
  }
}

/**
 * 阶段进度历史记录
 */
export interface StageHistory {
  id: string;
  stageId: string;
  projectId: string;
  oldCompletionPercentage: number | null;
  newCompletionPercentage: number;
  changedBy: string;
  changedByName: string;
  changedAt: string;
}

/**
 * 获取阶段进度变更历史
 */
export async function getStageHistory(
  projectId: string,
  stageId: string
): Promise<StageHistory[]> {
  try {
    const response = (await apiClient.get(
      `/projects/${projectId}/stages/${stageId}/history`
    )) as unknown as {
      success: boolean;
      data: StageHistory[];
      error?: string;
    };
    if (response.success && response.data) {
      return response.data;
    }
    logger.warn('获取阶段历史失败', response);
    return [];
  } catch (error: unknown) {
    logger.error('获取阶段历史失败', error);
    return [];
  }
}

/**
 * 项目简要信息(用于项目筛选器)
 */
export interface ProjectBrief {
  id: string;
  name: string;
  type: string;
  status: string;
  frameworkName?: string; // 仅计件制项目有此字段
  createdAt: string; // 创建时间，用于排序
}

/**
 * 获取所有项目列表(不分页)
 * 用于全局工时日历的项目筛选器
 */
export async function getAllProjects(params?: {
  search?: string;
}): Promise<ProjectBrief[]> {
  const response = (await apiClient.get('/projects/all', {
    params,
  })) as unknown as {
    success: boolean;
    data: ProjectBrief[];
  };
  return response.data || [];
}

/**
 * 检查项目是否有待审批的申请
 * @param projectId 项目ID
 * @param approvalType 审批类型（'project_post_record' | 'project_change'）
 * @returns 是否有待审批的申请
 */
export async function checkPendingApproval(
  projectId: string,
  approvalType: 'project_post_record' | 'project_change'
): Promise<boolean> {
  const response = (await apiClient.get(`/projects/${projectId}/check-pending-approval`, {
    params: { type: approvalType },
  })) as unknown as {
    success: boolean;
    data: { hasPending: boolean };
  };
  return response.data?.hasPending || false;
}
